import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { enumEntityModelCode } from '@app/data-layer/enums/entities';
import { enumEntityType } from '@app/data-layer/enums/isf';
import { AddressModel, ShipmentModel } from '@app/data-layer/models/common';
import { EntityModel } from '@app/data-layer/models/entities';
import { IsfModel } from '@app/data-layer/models/isf';
import { ShipmentsV2Repository } from '@app/data-layer/repositories';
import { FormHelper } from '@helpers/form.helper';
import { UtilsHelper } from '@helpers/utils-helper';
import { ISectionFormField } from '@interfaces/section-form-field';
import { TranslateService } from '@ngx-translate/core';
import { GenericService } from '@services/generic.service';
import { IErrorForm } from '@shared/form-wrapper/interfaces';
import { IsfOcrQuickFixService, IsfStateService } from '@shared/isf-mutual/services';
import { MissingFieldsComponent } from '@shared/missing-fields/components';
import { IdentifierPipe } from '@shared/pipes-shared/pipes';
import { merge, of, ReplaySubject } from 'rxjs';
import { debounceTime, distinctUntilChanged, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';

@Component({
  selector: 'app-isf-ocr-quick-fix-consignee-details',
  templateUrl: './isf-ocr-quick-fix-consignee-details.component.html',
  styleUrls: ['./isf-ocr-quick-fix-consignee-details.component.scss'],
})
export class IsfOcrQuickFixConsigneeDetailsComponent implements OnInit, OnDestroy {
  @ViewChild(MissingFieldsComponent, { static: false }) public missingFieldsModal: MissingFieldsComponent;
  @Input() public consigneeFields: ISectionFormField[];

  @Output() public dataSaved: EventEmitter<void> = new EventEmitter();

  public isf: IsfModel;
  public formGroup: UntypedFormGroup;
  public displayErrors: Map<string, string> = new Map<string, string>();
  public formErrors: { [name: string]: IErrorForm };

  public consigneeDisplayFields: ISectionFormField[] = [];
  public enumEntityModelCode: typeof enumEntityModelCode = enumEntityModelCode;
  public enumEntityType: typeof enumEntityType = enumEntityType;
  public isEditMode: boolean = false;
  public isDesktop: boolean = false;
  public importer: EntityModel;

  private isInitialFormChange: boolean = true;
  private isUsedEntityChange: boolean = false;

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(
    private fh: FormHelper,
    private cdr: ChangeDetectorRef,
    private genericService: GenericService,
    private identifierPipe: IdentifierPipe,
    private translateService: TranslateService,
    private shipmentsV2Repository: ShipmentsV2Repository,
    private isfOcrQuickFixService: IsfOcrQuickFixService,
    private isfStateService: IsfStateService,
  ) {}

  ngOnInit(): void {
    const isf = this.isfOcrQuickFixService.getIsf();
    this.syncIsf(isf);

    this.initializeFormGroup();

    this.isEditMode = this.isAnyFormBlockFieldEmpty(this.consigneeFields, this.formGroup);

    this.fh.updateEntityValidation(true, this.formGroup);

    this.genericService.isDesktop$.pipe(takeUntil(this.destroy$)).subscribe((isDesktop) => {
      this.isDesktop = isDesktop;
    });

    this.setFieldsProperties();

    this.populateDisplayErrors(this.formGroup);
    this.createFormErrors();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  public isAnyFormBlockFieldEmpty(blockFields: ISectionFormField[], fg: UntypedFormGroup): boolean {
    let isFgInvalid: boolean = false;
    Object.keys(fg.controls).forEach((key) => {
      if (fg.controls[key].invalid) {
        isFgInvalid = true;
        return;
      }
    });

    let invalidFields = blockFields.find((field) => fg.get(field.name)?.invalid || !fg.get(field.name)?.value || isFgInvalid);
    return !!invalidFields;
  }

  public onBlockEdit(isShowEditBlock: boolean) {
    this.isEditMode = isShowEditBlock;
    this.formGroup.markAllAsTouched();
    this.formGroup.updateValueAndValidity();
  }

  public onSubmit(isNeedCheck: boolean = true): void {
    const consigneeInvalidFields = this.fh.getInvalidFieldsByCode(this.formGroup.value, enumEntityModelCode.CONSIGNEE);
    if (isNeedCheck && Object.keys(consigneeInvalidFields).length > 0) {
      this.fh.markAsTouched(this.formGroup);
      this.openMissingFieldsModal(consigneeInvalidFields);
      return;
    }

    this.updateOrCreateConsignee()
      .pipe(takeUntil(this.destroy$))
      .subscribe((isf) => {
        this.isfOcrQuickFixService.setIsf(isf);
        this.isfStateService.updateCurrentIsf(isf);
        this.dataSaved.emit();
      });
  }

  public onClearForm(): void {
    this.formGroup.reset();
  }

  private openMissingFieldsModal(data) {
    this.missingFieldsModal.openCustomModal(data);
  }

  private setFieldsProperties() {
    const consignee = this.isf?.shipment?.consignee;

    const addressFieldMissingPartsErrorText = this.addressFieldMissingPartsErrorText();
    const consigneeAddressDisplayLine = !!addressFieldMissingPartsErrorText ? '' : this.getAddressLines(consignee?.address);
    const consigneeIdentifierDisplayLine = this.getIdentifierLine(consignee);
    const nameField = this.consigneeFields.find((field) => field.name === 'name');

    this.consigneeDisplayFields.push(
      {
        ...nameField,
        type: 'text',
        label: 'LABELS.NAME',
        name: 'name',
        value: consignee?.name,
      },
      {
        type: 'text',
        label: 'LABELS.ADDRESS',
        name: 'address',
        value: consigneeAddressDisplayLine,
      },
      {
        type: 'text',
        label: 'LABELS.ID_NUMBER',
        name: 'identifier',
        value: consigneeIdentifierDisplayLine,
      },
    );

    this.cdr.markForCheck();
  }

  private populateDisplayErrors(fg: UntypedFormGroup) {
    Object.keys(fg.controls).forEach((fieldName) => {
      switch (fieldName) {
        case 'name':
          this.displayErrors.set(fieldName, this.fieldMissingErrorText(fieldName, fg));
          break;
        case 'address':
          this.displayErrors.set(fieldName, this.addressFieldMissingPartsErrorText());
          break;
        default:
          break;
      }
    });
  }

  private createFormErrors(): void {
    this.formErrors = this.fh.createErrorsForIsfFormGroup();
  }

  private fieldMissingErrorText(fieldName: string | string[], fg: UntypedFormGroup): string {
    return !fg.get(fieldName)?.value ? 'LABELS.MISSING' : '';
  }

  private addressFieldMissingPartsErrorText(): string {
    const consigneeAddressErrors = this.isf?.shipment?.consignee?.address?.validationMessages || {};
    const missingAddressPartsText = !UtilsHelper.isObjectEmpty(consigneeAddressErrors) ? 'BACK_ERROR.MISSING_ADDRESS_PARTS' : '';
    return missingAddressPartsText;
  }

  private getAddressLines(data: AddressModel): string {
    if (!data) {
      return '';
    }

    const state = data.state ? data.state.description : null;
    const country = data.country ? data.country.description : null;
    const address = [data?.line1, data?.line2, data?.line3]
      .filter((part) => !!part)
      .join('\n')
      .trim();

    const line1 = [address, data.city]
      .filter((part) => !!part)
      .join(', ')
      .trim();
    const line2 = [country, state]
      .filter((part) => !!part)
      .join(', ')
      .trim();

    return line1 + line2;
  }

  private updateOrCreateConsignee() {
    const consignee: EntityModel = this.formGroup.value;
    const consigneeId = this.formGroup.get('id').value;

    return of(consignee).pipe(
      switchMap((entity) => {
        if (!entity.id || this.isPreviouslySavedAsImporter(this.formGroup, 'consigneeId')) {
          entity.id = 0;
          entity.addressId = 0;
          entity.address.id = 0;
          entity.entityCode = enumEntityModelCode.CONSIGNEE;
          return this.isfStateService.createEntity(entity);
        } else {
          return this.isfStateService.updateEntity(entity);
        }
      }),
      switchMap((entity) => {
        if (
          entity?.entityCode === enumEntityModelCode.CONSIGNEE &&
          (!this.isf.shipment?.consignee || this.isPreviouslySavedAsImporter(this.formGroup, 'consigneeId'))
        ) {
          const data = {
            ...this.isf.shipment,
            consignee: {
              ...entity,
              entityCode: enumEntityModelCode.CONSIGNEE,
            },
            consigneeId: entity.id,
          };

          return this.updateShipment(data);
        } else {
          return this.updateShipment({
            ...this.isf.shipment,
            consignee,
            consigneeId,
          });
        }
      }),
      switchMap((newShipment) => this.isfStateService.updateShipment(newShipment)),
      switchMap((shipment) => {
        return this.isfStateService.updateIsf({
          ...this.isf,
          shipment,
        });
      }),
      switchMap((isf) => this.isfStateService.loadIsf(isf.referenceNumber)),
      tap((isf) => {
        this.syncIsf(isf);
      }),
    );
  }

  private updateShipment(newShipmentData?: Partial<ShipmentModel>) {
    if (!newShipmentData) {
      newShipmentData = { ...this.isf?.shipment };
    }

    return this.shipmentsV2Repository.update(this.isf?.shipment?.id, newShipmentData);
  }

  private initializeFormGroup() {
    this.formGroup = this.fh.createEntityModelFormGroup(enumEntityModelCode.CONSIGNEE);

    this.formGroup.patchValue(this.isf?.shipment?.consignee);

    this.formGroup
      .get('id')
      .valueChanges.pipe(takeUntil(this.destroy$), distinctUntilChanged(), startWith(this.formGroup.get('id').value))
      .subscribe((consigneeId) => {
        this.applyImporterOrConsignee(consigneeId);
      });

    merge(
      this.formGroup.get('entityIdentifier').valueChanges,
      this.formGroup.get('entityIdentifierQualifier').valueChanges,
      this.formGroup.get('name').valueChanges,
      this.formGroup.get('address').valueChanges,
    )
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe((_) => {
        if (this.isInitialFormChange) {
          this.isInitialFormChange = false;
          return;
        }

        const isPatchNewIdRequired =
          this.importer && this.formGroup.get('id').value === this.importer.id && !this.isInitialFormChange && !this.isUsedEntityChange;

        if (isPatchNewIdRequired) {
          const isConsigneeIdAsImporterId = this.isf?.shipment?.consignee?.id === this.importer.id;
          this.formGroup.get('id').patchValue(!isConsigneeIdAsImporterId ? this.isf?.shipment?.consignee?.id : 0);
        }

        this.isUsedEntityChange = false;
      });

    if (this.formGroup.invalid) {
      this.formGroup.markAsDirty();
      this.formGroup.markAllAsTouched();
    }
  }

  private getIdentifierLine(entity: EntityModel) {
    if (!entity?.entityIdentifierQualifier && !entity?.entityIdentifier) {
      return;
    }

    const identifierQualifierText = this.translateService.instant(`IDENTIFIER_QUALIFIER.${entity.entityIdentifierQualifier}`);
    const identifierText = this.identifierPipe.transform(entity.entityIdentifier);
    return `${identifierQualifierText} ${identifierText}`;
  }

  private applyImporterOrConsignee(id: number) {
    if (this.importer && id === this.importer.id) {
      this.isUsedEntityChange = true;
      this.fh.patchIsfEntity(this.formGroup, this.importer);
    }
  }

  private syncIsf(isf: IsfModel) {
    this.isf = isf;
    this.importer = this.isf?.importer;
  }

  private isPreviouslySavedAsImporter(formGroup: UntypedFormGroup, entityIdFieldName: string): boolean {
    const entityId = this.isf.shipment[entityIdFieldName];
    const importer = this.isf.importer;
    const isEntityFgSameAsImporter = UtilsHelper.formMatchesObject(formGroup, importer);
    return importer?.id === entityId && !isEntityFgSameAsImporter;
  }
}
